/**
 * @license Copyright (c) 2003-2025, CKSource.
 */

// The editor creator to use.
import ClassicEditorBase from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';

import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';

import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';

import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import Link from '@ckeditor/ckeditor5-link/src/link';

import List from '@ckeditor/ckeditor5-list/src/list';
import TodoList from '@ckeditor/ckeditor5-list/src/todolist';

import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import SimpleUploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter';
import Notification from '@ckeditor/ckeditor5-ui/src/notification/notification';

import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';

import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak';

import { Mention } from '@ckeditor/ckeditor5-mention';

// Font & Highlight
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor';
import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight';

// Emoji (3rd party)
import {
	Emoji, EmojiActivity, EmojiFlags, EmojiFood, EmojiNature, EmojiObjects,
	EmojiPeople, EmojiPlaces, EmojiSymbols
} from '@phudak/ckeditor5-emoji/src';

// Your custom plugins
import EmojiAfterInsertWrap from './plugins/emoji-after-insert-wrap.plugin';
import MentionNoSpacePlugin from './plugins/mention-no-space.plugin';
import EnterToSendPlugin from './plugins/enter-to-send.plugin';
import SendModeSwitchPlugin from './plugins/send-mode-switch.plugin';
import BlockPasteDropImagesPlugin from './plugins/block-paste-drop-images.plugin';
import AttachmentButtonPlugin from './plugins/attachment-button.plugin';
import FullscreenTogglePlugin from './plugins/fullscreen-toggle.plugin';
import FunPickerPlugin from './plugins/fun-picker.plugin';

export default class ClassicEditor extends ClassicEditorBase {}

// Plugins to include in the build.
ClassicEditor.builtinPlugins = [
	Essentials,
	Autoformat,
	Bold,
	Italic,
	Underline,
	Strikethrough,
	BlockQuote,
	Mention,
	GeneralHtmlSupport,
	Heading,

	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,

	Indent,
	Link,

	List,
	TodoList,

	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	SimpleUploadAdapter,
	Notification,

	Table,
	TableToolbar,
	TextTransformation,
	PageBreak,

	// font & highlight
	FontColor,
	FontBackgroundColor,
	FontFamily,
	FontSize,
	Highlight,

	// emoji
	Emoji,
	EmojiActivity,
	EmojiFlags,
	EmojiFood,
	EmojiNature,
	EmojiObjects,
	EmojiPeople,
	EmojiPlaces,
	EmojiSymbols,

	// custom
	EmojiAfterInsertWrap,
	SendModeSwitchPlugin,
	BlockPasteDropImagesPlugin,
	AttachmentButtonPlugin,
	FullscreenTogglePlugin,
	MentionNoSpacePlugin,
	EnterToSendPlugin,
	FunPickerPlugin
];

ClassicEditor.defaultConfig = {
	toolbar: {
		items: [
			// 'attachment',
			'emoji',
			// 'fun',
			'bold',
			'italic',
			'link',
			'strikethrough',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bulletedList',
			'numberedList',
			'todoList',
			'|',
			'uploadImage',
			// 'pageBreak',
			// 'blockQuote',
			'insertTable',
			'mediaEmbed',
			'undo',
			'redo',
			'|',
			'fullscreen'
			// '|',
			// 'sendMode'
		]
	},

	// Emoji categories (optional)
	emoji: {
		categories: [ 'symbols', 'people', 'nature', 'food', 'travel', 'activities', 'objects', 'flags' ]
	},
	funPicker: {
		provider: 'tenor',
		apiKey: 'AIzaSyCTJbGXJEyMSEf7Z5t5uhVdFr34XvySGxM',
		limit: 24,
		showVideos: true
	},
	enterToSend: {
		enabled: true,
		mode: 'enterSends' // or 'shiftEnterSends' | 'buttonOnly'
		// callback: (editor) => this.SendChatClick(),
		// onModeChange: (mode) => { /* persist to settings if you want */ }
	},
	placeholder: '',
	link: {
		addTargetToExternalLinks: true
	},
	mediaEmbed: {
		previewsInData: true
	},
	image: {
		resizeOptions: [
			{
				name: 'resizeImage:original',
				value: null,
				icon: 'original'
			},
			{
				name: 'resizeImage:50',
				value: '50',
				icon: 'medium'
			},
			{
				name: 'resizeImage:75',
				value: '75',
				icon: 'large'
			}
		],
		toolbar: [
			'imageStyle:inline',
			'imageStyle:block',
			'imageStyle:side',
			'|',
			'resizeImage:50',
			'resizeImage:75',
			'resizeImage:original',
			'|',
			'toggleImageCaption',
			'imageTextAlternative'
		]
	},
	table: {
		contentToolbar: [ 'tableColumn', 'tableRow', 'mergeTableCells' ]
	},
	htmlSupport: {
		allow: [
			{
				name: 'span',
				classes: true,
				attributes: true,
				styles: true
			},
			{ name: 'video', attributes: true, classes: true, styles: true },
			{ name: 'source', attributes: true, classes: true, styles: true }
		]
	},

	// Optional: block paste/drag of images but still let text/mentions through (your plugin handles this)
	blockPasteDropImages: {
		enabled: true,
		blockPaste: true,
		blockDrop: true
	},

	language: 'en'
};
